import {
  IconAddressBook,
  IconBooks,
  IconFolder,
  IconGraph,
  IconPlugConnected,
  IconRobot,
  IconUser,
  IconUsers,
  IconBrandHipchat,
  IconReceipt,
  IconLocationDollar,
  IconTransactionDollar,
  IconCreditCard,
  IconClipboardList,
  IconShoppingBag,
  IconCash,
  IconActivity,
  IconBrandMeta,
  IconChartLine,
  IconChartBar,
  IconBrandInstagram,
  IconBrain,
  IconFileAnalytics,
  IconBrandTelegram,
  IconClipboardSearch,
  IconDeviceMobile,
  IconShieldHalfFilled,
  IconUserCheck,
  IconUserX,
  TablerIcon,
} from '@tabler/icons-react';
import { ReactElement } from 'react';

import config from '@/config';
import { AccountsPage } from '@/features/accounts';
import { AIPromptTemplatesPage } from '@/features/aiPromptTemplates';
import { AiReportPage, AiReportPromptPage, AiReportsPage } from '@/features/aiReports';
import {
  Managers as ManagersAnalytics,
  Requests as RequestsAnalytics,
  Triggers as TriggersAnalytics,
  NewDialogs as NewDialogsAnalytics,
  Instagram as InstagramAnalytics,
  Base as BaseAnalytics,
} from '@/features/analytics';
import { BotAllowedUsersPage, BotTelegramPagesView } from '@/features/bot';
import { Chat } from '@/features/chat';
import {
  PaymentTypesPage,
  PaymentProvidersPage,
  TrafficSourcesPage,
  ServicesPage,
  GoodsPage,
} from '@/features/checks';
import { AddScriptsPage, BulkUploadPage, ContentPage } from '@/features/content';
import { DeviceTrackerAdminPage } from '@/features/device-check';
import { DictionariesPage } from '@/features/dictionaries';
import { Managers, ManagerDevices } from '@/features/managers';
import { UserDataDeletionAuditLogPage, UserDataDeletionPage } from '@/features/meta';
import { ObjectionResponsesPage } from '@/features/objectionResponses';
import { Page, Pages } from '@/features/pages';
import { PaymentsPage } from '@/features/payments';
import { Profiles, Profile } from '@/features/profiles';
import { ProphetPage, ProphetPages, KeywordRulesPage } from '@/features/prophet';
import { RolesPage } from '@/features/roles';

export type NavRoute = {
  path: string;
  element: ReactElement;
  permission?: string;
  navLabel?: string;
  icon?: TablerIcon;
  group?: string;
  parent?: string;
  /** For ungrouped navLabel routes: which nav slot to render in. */
  section?: NavSectionName;
  /** Hidden on prod until the matching backend ships. */
  isHideOnProd?: boolean;
};

export type NavGroup = {
  label: string;
  icon: TablerIcon;
};

export const NAV_GROUPS: Record<string, NavGroup> = {
  Integrations: { label: 'Integrations', icon: IconPlugConnected },
  Meta: { label: 'Meta', icon: IconBrandMeta },
  Bot: { label: 'Bot', icon: IconRobot },
  Analytics: { label: 'Analytics', icon: IconGraph },
  Checks: { label: 'Checks', icon: IconReceipt },
  Access: { label: 'Access', icon: IconShieldHalfFilled },
};

/** Defines the order in which groups and ungrouped sections appear in the sidebar. */
export const NAV_SLOTS = [
  { type: 'group', name: 'Integrations' },
  { type: 'group', name: 'Meta' },
  { type: 'section', name: 'people' },
  { type: 'group', name: 'Bot' },
  { type: 'section', name: 'chat' },
  { type: 'group', name: 'Analytics' },
  { type: 'section', name: 'payments' },
  { type: 'group', name: 'Checks' },
  { type: 'group', name: 'Access' },
  { type: 'section', name: 'content' },
  { type: 'section', name: 'dictionaries' },
] as const;

export type NavSlot = (typeof NAV_SLOTS)[number];
export type NavSectionName = Extract<NavSlot, { type: 'section' }>['name'];

export const NAV_ROUTES: NavRoute[] = [
  {
    path: '/pages/instagram',
    element: <Pages platform="instagram" />,
    permission: 'pages:meta',
    navLabel: 'Instagram',
    group: 'Integrations',
    icon: IconBrandInstagram,
  },
  {
    path: '/pages/telegram',
    element: <Pages platform="telegram" />,
    permission: 'pages:telegram',
    navLabel: 'Telegram',
    group: 'Integrations',
    icon: IconBrandTelegram,
  },
  {
    path: '/pages/instagram/:id',
    element: <Page platform="instagram" />,
    permission: 'pages:meta',
  },
  {
    path: '/pages/telegram/:id',
    element: <Page platform="telegram" />,
    permission: 'pages:telegram',
  },
  {
    path: '/meta/user-data-deletion',
    element: <UserDataDeletionPage />,
    permission: 'meta:user-data-deletion',
    navLabel: 'User Data Deletion',
    group: 'Meta',
    icon: IconUserX,
  },
  {
    path: '/meta/data-deletion-audit-log',
    element: <UserDataDeletionAuditLogPage />,
    permission: 'meta:data-deletion-audit-log',
    navLabel: 'Data Deletion Audit Log',
    group: 'Meta',
    icon: IconClipboardSearch,
  },
  {
    path: '/profiles',
    element: <Profiles />,
    permission: 'profiles',
    navLabel: 'Profiles',
    section: 'people',
    icon: IconAddressBook,
  },
  { path: '/profiles/:id', element: <Profile />, permission: 'profiles' },
  {
    path: '/managers',
    element: <Managers />,
    permission: 'managers',
    navLabel: 'Managers',
    section: 'people',
    icon: IconUser,
  },
  {
    path: '/managers/devices',
    element: <ManagerDevices />,
    permission: 'managers',
    navLabel: 'Manager devices',
    section: 'people',
    icon: IconDeviceMobile,
  },
  {
    path: '/chat',
    element: <Chat />,
    permission: 'chat',
    navLabel: 'Chat',
    section: 'chat',
    icon: IconBrandHipchat,
  },
  {
    path: '/payments',
    element: <PaymentsPage />,
    permission: 'payments',
    navLabel: 'Payments',
    section: 'payments',
    icon: IconCash,
  },

  {
    path: '/bot-pages/telegram',
    element: <BotTelegramPagesView />,
    permission: 'bot:pages-telegram',
    navLabel: 'Telegram',
    group: 'Bot',
    parent: 'Pages',
    icon: IconBrandTelegram,
  },

  {
    path: '/prophet/meta',
    element: <ProphetPages platform="meta" />,
    permission: 'bot:processors-instagram',
    navLabel: 'Meta',
    group: 'Bot',
    parent: 'Page Processors',
    icon: IconBrandInstagram,
  },
  {
    path: '/prophet/telegram',
    element: <ProphetPages platform="telegram" />,
    permission: 'bot:processors-telegram',
    navLabel: 'Telegram',
    group: 'Bot',
    parent: 'Page Processors',
    icon: IconBrandTelegram,
  },
  {
    path: '/prophet/meta/:id',
    element: <ProphetPage platform="meta" />,
    permission: 'bot:processors-instagram',
    group: 'Bot',
  },
  {
    path: '/prophet/telegram/:id',
    element: <ProphetPage platform="telegram" />,
    permission: 'bot:processors-telegram',
    group: 'Bot',
  },
  {
    path: '/prophet/keywords/meta/:id',
    element: <KeywordRulesPage platform="meta" />,
    permission: 'bot:processors-instagram',
    group: 'Bot',
  },
  {
    path: '/prophet/keywords/telegram/:id',
    element: <KeywordRulesPage platform="telegram" />,
    permission: 'bot:processors-telegram',
    group: 'Bot',
  },

  {
    path: '/bot/allowed-users',
    element: <BotAllowedUsersPage />,
    permission: 'bot:allowed-users',
    navLabel: 'Allowed Users',
    group: 'Bot',
    icon: IconUserCheck,
  },
  {
    path: '/ai/prompt-templates',
    element: <AIPromptTemplatesPage />,
    permission: 'bot:ai-prompt-templates',
    navLabel: 'AI Prompt Templates',
    group: 'Bot',
    icon: IconBrain,
  },
  {
    path: '/ai/prompt-templates/:aiId/objections',
    element: <ObjectionResponsesPage />,
    permission: 'bot:ai-prompt-templates',
    group: 'Bot',
  },

  {
    path: '/analytics/managers',
    element: <ManagersAnalytics />,
    permission: 'analytics:managers',
    navLabel: 'Managers',
    group: 'Analytics',
    icon: IconUser,
  },
  {
    path: '/analytics/requests',
    element: <RequestsAnalytics />,
    permission: 'analytics:requests',
    navLabel: 'Requests',
    group: 'Analytics',
    icon: IconRobot,
  },
  {
    path: '/analytics/triggers',
    element: <TriggersAnalytics />,
    permission: 'analytics:triggers',
    navLabel: 'Triggers',
    group: 'Analytics',
    icon: IconActivity,
  },
  {
    path: '/analytics/new-dialogs',
    element: <NewDialogsAnalytics />,
    permission: 'analytics:new-dialogs',
    navLabel: 'New Dialogs',
    group: 'Analytics',
    icon: IconChartLine,
  },
  {
    path: '/analytics/instagram',
    element: <InstagramAnalytics />,
    permission: 'analytics:instagram',
    navLabel: 'Instagram',
    group: 'Analytics',
    icon: IconBrandInstagram,
  },
  {
    path: '/analytics/base',
    element: <BaseAnalytics />,
    permission: 'analytics:base',
    navLabel: '1L-Baza',
    group: 'Analytics',
    icon: IconChartBar,
  },
  {
    path: '/ai-reports',
    element: <AiReportsPage />,
    permission: 'ai-reports',
    navLabel: 'AI-reports',
    group: 'Analytics',
    icon: IconFileAnalytics,
    // DEV-3412: hide until Ready for deploy
    isHideOnProd: true,
  },
  {
    path: '/ai-reports/prompts',
    element: <AiReportPromptPage />,
    permission: 'ai-reports:prompts',
    isHideOnProd: true,
  },
  {
    path: '/ai-reports/:reportId',
    element: <AiReportPage />,
    permission: 'ai-reports',
    isHideOnProd: true,
  },

  {
    path: '/checks/traffic-sources',
    element: <TrafficSourcesPage />,
    permission: 'checks:traffic-sources',
    navLabel: 'Traffic Sources',
    group: 'Checks',
    icon: IconLocationDollar,
  },
  {
    path: '/checks/payment-types',
    element: <PaymentTypesPage />,
    permission: 'checks:payment-types',
    navLabel: 'Payment Types',
    group: 'Checks',
    icon: IconTransactionDollar,
  },
  {
    path: '/checks/payment-providers',
    element: <PaymentProvidersPage />,
    permission: 'checks:payment-providers',
    navLabel: 'Payment Providers',
    group: 'Checks',
    icon: IconCreditCard,
  },
  {
    path: '/checks/services',
    element: <ServicesPage />,
    permission: 'checks:services',
    navLabel: 'Services',
    group: 'Checks',
    icon: IconClipboardList,
  },
  {
    path: '/checks/goods',
    element: <GoodsPage />,
    permission: 'checks:goods',
    navLabel: 'Goods',
    group: 'Checks',
    icon: IconShoppingBag,
  },
  {
    path: '/checks/device-tracker',
    element: <DeviceTrackerAdminPage />,
    permission: 'checks:device-tracker',
    navLabel: 'Device Tracker',
    group: 'Checks',
    icon: IconDeviceMobile,
  },

  {
    path: '/access/accounts',
    element: <AccountsPage />,
    permission: 'access:manage',
    navLabel: 'Accounts',
    group: 'Access',
    icon: IconUsers,
  },
  {
    path: '/access/roles',
    element: <RolesPage />,
    permission: 'access:manage',
    navLabel: 'Roles',
    group: 'Access',
    icon: IconShieldHalfFilled,
  },

  {
    path: '/content',
    element: <ContentPage />,
    permission: 'content',
    navLabel: 'Content',
    section: 'content',
    icon: IconFolder,
  },
  {
    path: '/dictionaries',
    element: <DictionariesPage />,
    permission: 'dictionaries:manage',
    navLabel: 'Dictionaries',
    section: 'dictionaries',
    icon: IconBooks,
  },
  {
    path: '/content/add/text/:platform/:pageId',
    element: <AddScriptsPage />,
    permission: 'content',
  },
  {
    path: '/content/add/:mediaType/:platform/:pageId',
    element: <BulkUploadPage />,
    permission: 'content',
  },
];

const enabledNavRoutes = NAV_ROUTES.filter((route) => !(config.isProd && route.isHideOnProd));

/** Returns routes that should be active in the current environment, filtering out isHideOnProd entries on prod. */
export const getEnabledNavRoutes = (): NavRoute[] => enabledNavRoutes;
